/* istanbul ignore file */
// REMOVE ABOVE WHEN IMPLEMENTING CHANGE HANDLERS
import React, { ChangeEvent, useEffect, useState } from 'react';
import { EntryField } from '@noths/polaris-client-ribbons-design-system';
import { _Omit } from '@noths/polaris-dev-ts-recipes';

import { PersonalisationProductOption } from 'src/types/product';
import {
  MAX_CHARACTERS_COPY,
  NO_EMOJIS_ALLOWED_COPY,
  REQUIRED_INPUT_COPY,
} from '../../constants/copy';
import { containsEmoji, getTextCaseNotice } from '../../utils';

export interface InputOnChangeArgs {
  value: string;
  isValid: boolean;
}

export type OwnProps = _Omit<PersonalisationProductOption, 'type'> & {
  displayValidation?: boolean;
  required?: boolean;
  onChange: ({ value, isValid }: InputOnChangeArgs) => void;
};

interface ContainerProps {
  error: boolean;
  value?: string;
}

type Props = OwnProps & ContainerProps;

interface BlurHandlerProps extends Pick<OwnProps, 'onChange' | 'maxCharacters'> {
  e: ChangeEvent<HTMLTextAreaElement>;
}

const validateInputField = (value: string, maxCharacters: number) => {
  if (containsEmoji(value)) {
    return false;
  }

  if (value.length <= maxCharacters) {
    return true;
  }

  return false;
};

/**
 * The majority of the input validation code happens here after blur is called
 * the onChange isValid boolean will handle preventing submit
 */
const handleBlur = ({ e, onChange, maxCharacters }: BlurHandlerProps) => {
  const { value } = e.target;

  onChange({ value, isValid: validateInputField(value, maxCharacters) });
};

export const Input = ({
  error,
  value = '',
  required = false,
  onChange,
  maxCharacters,
  minCharacters,
  allowedCase,
  name,
  id,
}: Props) => {
  const [stateValue, setStateValue] = useState(value);
  const [errorMessage, setErrorMessage] = useState(error ? REQUIRED_INPUT_COPY : '');

  useEffect(() => {
    const hasInvalidInputLength = stateValue.length > maxCharacters;

    if (hasInvalidInputLength) {
      setErrorMessage(MAX_CHARACTERS_COPY);
    } else if (containsEmoji(stateValue)) {
      setErrorMessage(NO_EMOJIS_ALLOWED_COPY);
    } else if (error) {
      setErrorMessage(REQUIRED_INPUT_COPY);
    } else {
      setErrorMessage('');
    }
  }, [error, maxCharacters, stateValue]);

  // Only used if value prop gets updated to refresh input stateValue
  useEffect(() => {
    if (value !== stateValue) {
      setStateValue(value);
      onChange({ value, isValid: validateInputField(value, maxCharacters) });
    }
  }, [value]);

  return (
    <EntryField
      value={stateValue}
      id={id.toString()}
      placeholder={`Max ${maxCharacters} characters`}
      error={errorMessage}
      description={allowedCase ? getTextCaseNotice(allowedCase) : ''}
      onBlur={(e) => handleBlur({ e, maxCharacters, onChange })}
      required={required}
      onChange={(e) => setStateValue(e.target.value)}
      minLength={minCharacters}
      name={name}
      feedback={{
        name: errorMessage || '',
        value: maxCharacters - stateValue.length,
      }}
    />
  );
};
